import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    FormControl,
    FormLabel,
    useToast,
    Checkbox,
    Stack
  } from '@chakra-ui/react';
  import React, { useState, useEffect } from 'react';
  import axios from 'axios';
  import { useApi } from 'components/hooks/useApi';
  import { Category } from 'components/types/categories';
  
  interface EditCategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    category: Category | null;
    accessToken: string;
    tenant: string | number;
    refreshCategories: () => void;
  }
  
  const EditCategoryModal: React.FC<EditCategoryModalProps> = ({
    isOpen,
    onClose,
    category,
    accessToken,
    tenant,
    refreshCategories,
  }) => {
    const toast = useToast();
    const apiUrl = useApi();
  
    // Local state for form fields
    const [name, setName] = useState<string>('');
    const [batchCategory, setBatchCategory] = useState<boolean>(false);
    const [expiryCategory, setExpiryCategory] = useState<boolean>(false);
  
    // Populate state when the modal opens with the selected category
    useEffect(() => {
      if (category) {
        setName(category.name);
        setBatchCategory(category.batch_category);
        setExpiryCategory(category.expiry_category);
      }
    }, [category]);
  
    const handleSave = async () => {
      if (!category) return;
      try {
        // Build payload with the updated fields
        const payload = {
          Categories: [
            {
              id: category.id,
              name: name,
              batch_category: batchCategory,
              expiry_category: expiryCategory,
            },
          ],
        };
  
        const response = await axios.put(`${apiUrl}/categories`, payload, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          params: { tenant },
        });
  
        if (response.status === 200 || response.status === 204) {
          toast({
            title: 'Category updated successfully',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onClose();
          refreshCategories(); // refresh parent data
        } else {
          throw new Error('Update failed');
        }
      } catch (error: any) {
        console.error(error);
        toast({
          title: 'Failed to update category',
          description: error.response?.data?.message || 'An error occurred.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    };
  
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Category</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Category Name</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormControl>
  
            {/* Checkbox controls for batch_category, expiry_category */}
            <Stack spacing={3}>
              <Checkbox
                isChecked={batchCategory}
                onChange={(e) => setBatchCategory(e.target.checked)}
              >
                Batched?
              </Checkbox>
              <Checkbox
                isChecked={expiryCategory}
                onChange={(e) => setExpiryCategory(e.target.checked)}
              >
                Expires?
              </Checkbox>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="brand" onClick={handleSave}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };
  
  export default EditCategoryModal;